import { useState, useEffect } from "react";
import axiosInstance from '../../../Config/axiosInstance';
import {Container, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, MenuItem, Button, Grid, 
    Typography, CircularProgress, Alert, Box, Menu, ListItemIcon, ListItemText, OutlinedInput, Select, FormControl, 
    InputLabel} from '@mui/material';
import { Save, PictureAsPdf, GetApp, Assessment} from '@mui/icons-material';
import './ProductReports.css';
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import ExcelJS from 'exceljs';
import {saveAs} from "file-saver";
import logoImg from '../../../Images/logo_pmpc.png'


const ProductReports = () => {
    const [products, setProducts] = useState([]);
    const [selectedProduct, setSelectedProduct] = useState('');
    
    const [reportData, setReportData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [loadingOptions, setLoadingOptions] = useState(false);
    const [anchorEl, setAnchorEl] = useState(null);
    const [error, setError] = useState('');
    const [reportGenerated, setReportGenerated] = useState(false);

    useEffect(() => {
        const getData = async () => {
            try {
                setLoadingOptions(true);
                const response = await axiosInstance.get('/product/');
                setProducts(response.data);
                console.log(response.data);
            }
            catch (err) {
                console.error('Error fetching filter options: ', err);
                setError('Failed to load filter options');
                setProducts([]);
            }
            finally {
                setLoadingOptions(false);
            }
        }

        getData();
    }, []);

    const handleFilterChange = (value) => {
        setSelectedProduct(value);
        setReportGenerated(false);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (selectedProduct === '') {
            setError('Please select the product.');
            setReportGenerated(false);
            setReportData(null);
            return;
        }

        setLoading(true);
        setError('');

        try {
            const params = {
                product: selectedProduct,
            };
            const response = await axiosInstance.get('/product-report/', { params });
            setReportData(response.data);
            setReportGenerated(true);

        } catch (err) {
            setError(err.response?.data?.error || 'Failed to generate report');
            setReportData(null);
            setReportGenerated(false);
        } finally {
            setLoading(false);
        }
    };

    const handleClick = (event) => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const formatNumber = (num) => {
        if (num == null || num === undefined) return '0.00';
        return new Intl.NumberFormat('en-EN', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        }).format(num);
    };

    const exportToExcel = async (data = null, customTitle = null) => {
        if (!reportData || !reportData.products || reportData.products.length === 0) {
            alert('No data to export!');
            return;
        }

        const currentTime = new Date().toLocaleString('ro-RO');
        
        const exportData = data || reportData;
        const title = customTitle || `Product Report - ${reportData.product_name} - ${new Date().toLocaleString('ro-RO')}`;

        const workbook = new ExcelJS.Workbook();
        const sheet = workbook.addWorksheet('Report');
        
        //logo
        try {
            sheet.mergeCells('A1:A4');
            const logo = workbook.addImage({
                buffer: await fetch(logoImg).then(res => res.arrayBuffer()),
                extension: 'png',
            });
            sheet.addImage(logo, {
                tl: { col: 0, row: 0 },
                br: { col: 1, row: 4 }
            });
        } catch (error) {
            console.warn('Logo could not be loaded:', error);
        }

        //titlu 
        sheet.mergeCells('B1:C4');
        const titleCell = sheet.getCell('B1');
        titleCell.value = title;
        titleCell.font = { size: 14, bold: true };
        titleCell.alignment = {
            vertical: 'middle',
            horizontal: 'center',
            wrapText: true
        };

        sheet.getCell("A5").value = "Selected Product: " + reportData.product_name;
        sheet.getCell("A6").value = "Number of POs: " + reportData.count;
        sheet.getCell("A7").value = "Date of Report: " + currentTime;

        //rand liber dupa titlu
        sheet.insertRow(5, []);

        //header pentru primul tabel
        const headerRowNumber = 10;
        const headers = ["PO Number", "Supplier", "Unit price"];
        sheet.getRow(headerRowNumber).height = 30;
        
        const headerRow = sheet.getRow(headerRowNumber);
        headerRow.values = headers;
        
        //stil pentru header
        headerRow.font = { bold: true };
        headerRow.eachCell((cell) => {
            cell.alignment = {
                vertical: 'middle',
                horizontal: 'center',
                wrapText: true
            };
            cell.fill = {
                type: 'pattern',
                pattern: 'solid',
                fgColor: { argb: 'FFE8E8E8' }
            };
        });

        //date lunare
        exportData.products.forEach((product, index) => {
            const dataRowNumber = headerRowNumber + 1 + index;
            const row = sheet.getRow(dataRowNumber);
            row.height = 20;
            row.values = [
                product.purchaseOrder,
                product.supplier || 0,
                String(formatNumber(product.unitPrice)) + ' ' + String(product.currency) || 0
            ];
            
            //formatare numerica
            row.getCell(4).numFmt = '#,##0.00';
            row.getCell(4).alignment = { horizontal: 'right' };
        });

        //auto-width
        sheet.columns.forEach((column, columnIndex) => {
            let maxLength = 40;
            column.eachCell({includeEmpty: true}, (cell) =>{
                let cellValue = cell.value?.toString() || '';
                if(cell.numFmt === '0.00' && !isNaN(cell.value)){
                    cellValue = parseFloat(cell.value || 0).toFixed(2);
                }
                maxLength = Math.max(maxLength, cellValue.length);
            });
            
            if (columnIndex === 0) {
                column.width = 40
            }
            else {
                column.width = maxLength;
            }
        });

        //genereaza si salveaza fisierul
        const buffer = await workbook.xlsx.writeBuffer();
        const blob = new Blob([buffer], {
            type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
        });

        const fileName = `ProductReport_${reportData.product_name}_${currentTime}.xlsx`;
        saveAs(blob, fileName);
    };

    const exportPDF = async () => {
        if (!reportData || !reportData.products || reportData.products.length === 0) {
            alert('No data to export!');
            return;
        }

        const currentTime = new Date().toLocaleString('ro-RO');
        
        //pdf portrait
        const pdf = new jsPDF("p", "mm", "a4");
        const pdfWidth = pdf.internal.pageSize.getWidth();
        const pdfHeight = pdf.internal.pageSize.getHeight();
        const logo = "/logo_pmpc.png";

        //logo
        pdf.addImage(logo, "PNG", 8, 3, 28, 8);

        //dimensiuni elemente decorative
        const triangleWidth = 20;
        const totalWidth = pdfWidth * 0.25;
        const barWidth = totalWidth - triangleWidth;

        const barHeight = 1.2;     // bara scurtă + triunghi
        const secondBarHeight = 2; // bara lungă
        const barTop = 15;
        const barBottom = barTop + barHeight;

        //bara scurta
        pdf.setFillColor(23, 52, 88);
        const barOverlap = 1;
        pdf.rect(0, barTop, barWidth + barOverlap, barHeight, "F");

        //triunghi
        pdf.triangle(
        barWidth, barBottom,                 
        barWidth + triangleWidth, barBottom,  
        barWidth, barTop,                   
        "F"
        );

        //suprapunere 
        const overlap = 0.6;
        const headerLongBarTop = barBottom - overlap;

        //bara lunga full-width (header)
        pdf.rect(0, headerLongBarTop, pdfWidth, secondBarHeight, "F");

        const dottedLineY = headerLongBarTop + secondBarHeight;
        //titlu
        const reportTitle = `Product Report`;

        pdf.setFontSize(14);
        pdf.setFont("helvetica", "bold");
        pdf.text(reportTitle, pdfWidth / 2, dottedLineY + 12, {align: "center"});

        const contentTop = dottedLineY + 20;
        let currentY = contentTop;

        const columnStyles = {
            0: { cellWidth: 40 },
            1: { cellWidth: 80 },
            2: { cellWidth: 70, halign: 'right' },
        };

        //date lunare
        const monthlyTableHeaders = [
            ["PO Number", "Supplier", "Unit price"]
        ];

        const monthlyTableData = reportData.products.map(product => {
            return [
                product.purchaseOrder,
                product.supplier || 0,
                String(formatNumber(product.unitPrice)) + ' ' + String(product.currency) || 0
            ];
        });

        pdf.setFontSize(10);
        pdf.setFont("helvetica", "normal");
        pdf.text("Selected Product: " + reportData.product_name, 10, dottedLineY + 20, {align: "left"});
        pdf.text("Number of POs: " + reportData.count, 10, dottedLineY + 25, {align: "left"});
        pdf.text("Date of Report: " + currentTime, 10, dottedLineY + 30, {align: "left"});

        //adauga primul tabel
        autoTable(pdf, {
            startY: currentY + 15,
            head: monthlyTableHeaders,
            body: monthlyTableData,
            styles: {
                fontSize: 9, 
                cellPadding: 2,
                overflow: "linebreak",
                lineColor: [180, 180, 180],
                lineWidth: 0.25,
                valign: "middle",
                textColor: [0, 0, 0],
            },
            headStyles: {
                fillColor: [232, 232, 232],
                textColor: 0, 
                fontStyle: "bold",
                halign: "center",
            },
            columnStyles: columnStyles, 
            alternateRowStyles: {
                fillColor: [245, 247, 250]
            },
            margin: {left: 10, right: 10}, 
            tableWidth: 'auto',
            theme: 'grid',
            didDrawPage: function(data) {
                if (data.cursor) {
                    currentY = data.cursor.y;
                }
            }
        });
        
        currentY = pdf.lastAutoTable.finalY + 15;

        //footer pentru toate paginile
        const totalPages = pdf.internal.getNumberOfPages();
        
        for (let i = 1; i <= totalPages; i++) {
            pdf.setPage(i);
            
            pdf.setFillColor(23, 52, 88);

            const bottomOffset = 12; 
            const longBarTop = pdfHeight - bottomOffset; 

            //suprapunere
            const verticalOverlapFooter = 0.6;

            //bara lunga jos
            pdf.rect(
            0,
            longBarTop - verticalOverlapFooter + 0.1,
            pdfWidth,
            secondBarHeight,
            "F"
            );

            //bara scurta + triunghi
            const shortBarTop = longBarTop + secondBarHeight - verticalOverlapFooter;
            const shortBarBottom = shortBarTop + barHeight;

            //bara scurta 
            const barOverlapFooter = 1;
            pdf.rect(
            0,
            shortBarTop,
            barWidth + barOverlapFooter,
            barHeight,
            "F"
            );

            //triunghiul din partea dreapta jos
            pdf.triangle(
            barWidth, shortBarTop,                    
            barWidth + triangleWidth, shortBarTop,        
            barWidth, shortBarBottom,                      
            "F"
            );

            //pagina curenta/ total pagini
            pdf.setFontSize(7);
            pdf.setFont("helvetica", "normal");
            pdf.text("Generated by PMPC ERP", 10, pdfHeight - 3);
            const today = new Date();
            const currentDate = today.toLocaleDateString("ro-RO");
            pdf.text(`${currentDate} `, pdfWidth - 30, pdfHeight - 3);

            pdf.text(`Page ${i} of ${totalPages}`, pdfWidth / 2, pdfHeight - 3, { align: "center" });
        }

        const fileName = `ProductReport_${reportData.product_name}_${currentTime}.pdf`;

        //save pdf
        pdf.save(fileName);
    };

    return (
        <Container maxWidth={false} disableGutters className="proj-report project-report">
            <Container maxWidth={false} disableGutters className="project-container">
                <Typography variant="h4" gutterBottom marginTop="20px">
                    Product Report
                </Typography>
                
                <Paper className="filters-paper" sx={{ p: 2 }}>
                    <form onSubmit={handleSubmit}>
                        <Grid container spacing={2} alignItems="center">
                            <Grid item xs={12} md={9}>
                                <Box display="flex" flexWrap="wrap" alignItems="center" gap={2}>
                                    <FormControl 
                                        size="small" 
                                        className="project-code-select"
                                        sx={{ minWidth: 180 }}
                                        disabled={loadingOptions || products.length === 0}
                                    >
                                        <InputLabel>Product</InputLabel>
                                        <Select
                                            value={selectedProduct}
                                            onChange={(e) => handleFilterChange(e.target.value)}
                                            input={<OutlinedInput label="Product" />}
                                            MenuProps={{
                                                PaperProps: {
                                                    sx: {
                                                    width: 250,
                                                    '& .MuiMenuItem-root': {
                                                        fontSize: '0.75rem',
                                                        paddingTop: 0.25,
                                                        paddingBottom: 0.25,
                                                        whiteSpace: 'normal',
                                                        wordBreak: 'break-word',
                                                    },
                                                    '& .MuiCheckbox-root': {
                                                        padding: '2px',
                                                        marginRight: '4px',
                                                    },
                                                    },
                                                },
                                            }}
                                            >
                                            {products.length > 0 ? (
                                                products.map(option => (
                                                    <MenuItem key={option.id} value={option.id}>
                                                        <ListItemText primary={option.title} />
                                                    </MenuItem>
                                                ))
                                            ) : (
                                                <MenuItem disabled>
                                                    <ListItemText 
                                                        primary={loadingOptions ? "Loading..." : "No products available"} 
                                                    />
                                                </MenuItem>
                                            )}
                                        </Select>
                                    </FormControl>

                                    <Button 
                                        type="submit"
                                        variant="contained"
                                        color="primary"
                                        size="small"
                                        className="generate-button"
                                        startIcon={loading ? <CircularProgress size={20} /> : <Assessment />}
                                        disabled={loadingOptions || loading}
                                    >
                                        {loading ? 'Generating...' : 'Generate Report'}
                                    </Button>
                                </Box>
                            </Grid>

                            <Grid item xs={12} md={3}>
                                <Grid container spacing={1} justifyContent={{ xs: 'flex-start', md: 'flex-end' }}>
                                    <Grid item>
                                        <Button
                                            variant="contained"
                                            size="small"
                                            onClick={handleClick}
                                            className="save-button"
                                            startIcon={<Save />}
                                            disabled={!reportGenerated}
                                        >
                                            Save As
                                        </Button>
                                    </Grid>
                                </Grid>

                                <Menu 
                                    anchorEl={anchorEl} 
                                    open={Boolean(anchorEl)} 
                                    onClose={handleClose}
                                >
                                    <MenuItem onClick={() => {
                                        exportPDF();
                                        handleClose();
                                    }}>
                                        <ListItemIcon><PictureAsPdf fontSize="small" /></ListItemIcon>
                                        <ListItemText>Save as PDF</ListItemText>
                                    </MenuItem>
                                    <MenuItem onClick={() =>{
                                        exportToExcel();
                                        handleClose();
                                    }}>
                                        <ListItemIcon><GetApp fontSize="small" /></ListItemIcon>
                                        <ListItemText>Save as Excel</ListItemText>
                                    </MenuItem>
                                </Menu>
                            </Grid>
                        </Grid>

                        {error && (
                            <Alert severity="error" sx={{ mt: 2 }}>
                                {error}
                            </Alert>
                        )}
                    </form>
                </Paper>

                {reportData && reportGenerated && (
                    <>
                        <Box mt={2}>
                            <Typography color="text.secondary">
                                Selected product:{' '}
                                <Typography component="span" color="text.primary">
                                    {reportData.product_name}
                                </Typography>
                            </Typography>

                            <Typography color="text.secondary">
                                Number of POs:{' '}
                                <Typography component="span" color="text.primary">
                                    {reportData.count}
                                </Typography>
                            </Typography>
                        </Box>
                        
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, mt: 3 }}>
                            <Paper sx={{ p: 2 }}>
                                {reportData.count > 0 && reportData.products && reportData.products.length > 0 ? (
                                <TableContainer className="report-table-container">
                                    <Table size="small" sx={{ tableLayout: 'fixed' }}>
                                    <TableHead>
                                        <TableRow className="table-header">
                                        <TableCell className="table-header-cell">PO Number</TableCell>
                                        <TableCell className="table-header-cell">Supplier</TableCell>
                                        <TableCell className="table-header-cell">Unit price</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {reportData.products.map((product) => (
                                        <TableRow 
                                            key={product.purchaseOrder}
                                            hover
                                            sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                                        >
                                            <TableCell sx={{ width: '33%' }}>{product.purchaseOrder}</TableCell>
                                            <TableCell sx={{ width: '33%' }}>{product.supplier}</TableCell>
                                            <TableCell sx={{ width: '33%' }}>{formatNumber(product.unitPrice)} {product.currency}</TableCell>
                                        </TableRow>
                                        ))}
                                    </TableBody>
                                    </Table>
                                </TableContainer>
                                ) : (
                                <Alert severity="info" sx={{ mt: 2 }}>
                                    No PO found for the selected criteria.
                                </Alert>
                                )}
                            </Paper>
                        </Box>
                    </>
                )}
            </Container>
            {reportData && reportGenerated && (
                <Box className="report-footer">
                    <Typography variant="caption" className="footer-text">
                        Generated on {reportData.report_date || new Date().toLocaleString('ro-RO')} | {' '}
                        Product: {reportData.product_name}
                    </Typography>
                </Box>
            )}
        </Container>
    );
};

export default ProductReports;